import { CONFIG } from '@/config';
import {
  EconomicGroup,
  Families,
  Family,
  Project
} from '@/modules/business-plan/modules/approval/pages/ford-goal-settings/types';
import { ServiceHelper } from '@/utils/service.helper';
import * as moment from 'moment';
import { ITextValue } from '@/interfaces/key-value-interface';
import { SaleTypeEnum } from '@/enums/sale-type.enum';
import { NameValue, NameValues } from '@/modules/neodash/model/types/interfaces/NameValue';
import { GoalVsRegistrationType } from '@/modules/business-plan/modules/models/enums/goalVsRegistrationType';
import { IGoalVsRegistration } from '@/modules/business-plan/modules/report/types';
import { BpaStatusReport } from '@/modules/business-plan/modules/models/bpaStatusReport';

const baseUrlBpa = `${CONFIG.URL.NEODASH_API}/api/bpa`;
const baseUrlGroupBpa = (idProject: number) => `${baseUrlBpa}/${idProject}/group`;
const baseUrlBpaReport = (idProject: number) => `${baseUrlBpa}/${idProject}/report`;

export class ApprovalService {
  public moment = moment;

  public static GetProjects = async (): Promise<NameValues> => {
    const data = await ServiceHelper.mapToPromise<any[]>(`${baseUrlBpa}/projects`);
    return data.map(x => ({ name: x.name, value: parseInt(String(x.id)) })) as NameValues;
  };

  public static GetProject = (idProject: number): Promise<Project> =>
    ServiceHelper.mapToPromise<Project>(`${baseUrlBpa}/${idProject}`);

  public static GetProjectByYear = (year: number): Promise<Project> =>
    ServiceHelper.mapToPromise<Project>(`${baseUrlBpa}/${year}/reference`);

  public static ListFamilies = (idProject: number): Promise<Family[]> =>
    ServiceHelper.mapToPromise<Families>(`${baseUrlBpa}/${idProject}/families`)
      .then(families => families.sortBy(x => x.order));

  public static GetEconomicGroups = (idProject: number): Promise<Array<EconomicGroup>> =>
    ServiceHelper.mapToPromise<Array<EconomicGroup>>(`${baseUrlGroupBpa(idProject)}`);

  public static GetGoalDefinitionData = (idProject: number, idLocale: number, idGroup: number): Promise<EconomicGroup> =>
    ServiceHelper.mapToPromise<EconomicGroup>(`${baseUrlGroupBpa(idProject)}/${idLocale}/${idGroup}/detail`);

  public static SaveGoalSettings = (idProject: number, group: EconomicGroup): Promise<void> =>
    ServiceHelper.mapToPromise(`${baseUrlGroupBpa(idProject)}/save`, { method: 'post', parameters: group });

  public static GetBpaStatusReport = (idProject: number): Promise<Array<BpaStatusReport>> =>
    ServiceHelper.mapToPromise(`${baseUrlBpaReport(idProject)}/status`);

  public static SaveGoalSettingsPerMonth = (idProject: number, group: EconomicGroup): Promise<void> =>
    ServiceHelper.mapToPromise(`${baseUrlGroupBpa(idProject)}/save/perMonth`, { method: 'post', parameters: group });

  public static GetSaleChannels = (): Promise<ITextValue<number>[]> =>
    ServiceHelper.mapToPromise<ITextValue<number>[]>(`${CONFIG.URL.NEODASH_API}/api/neodash/saleChannels`);

  public static GetGoalVsRegistrationReport = (idProject: number, idStoreProject: number, saleType: SaleTypeEnum, typeInformation: GoalVsRegistrationType): Promise<IGoalVsRegistration> =>
    ServiceHelper.mapToPromise<IGoalVsRegistration>(`${baseUrlBpaReport(idProject)}/${idStoreProject}/${saleType}/${typeInformation}`);

  public static GetStoreProjects = async (idProject: number): Promise<NameValues> => {
    const data = await ServiceHelper.mapToPromise<any[]>(`${baseUrlBpaReport(idProject)}/filter/stores`);
    return data.map(x => ({ name: x.nameStore, value: x.id })) as NameValues;
  };

  public static GetLastMonthRegistration = (idProject: number, saleType: SaleTypeEnum): Promise<number> =>
    ServiceHelper.mapToPromise<number>(`${baseUrlBpa}/${idProject}/lastMonthWithRegistration/${saleType}`);

  public static DownloadBpaPacc = (prefixFileName: string, idProject: number, group: EconomicGroup): Promise<void> =>
    ServiceHelper.generateFile(
      `${baseUrlBpaReport(idProject)}/pacc/${group.idGroup}/${group.idLocale}`,
      `${prefixFileName}_${group.nameGroup}_${group.nameLocale}_${moment().format('YYYYMMDD_HHmmss')}.xlsx`);

  public static DownloadBpaPaccByName = (prefixFileName: string, idProject: number, nameGroup: string, nameLocale: string): Promise<void> =>
    ServiceHelper.generateFile(
      `${baseUrlBpaReport(idProject)}/pacc/group/${nameGroup}/locale/${nameLocale}`,
      `${prefixFileName}_${nameGroup}_${nameLocale}_${moment().format('YYYYMMDD_HHmmss')}.xlsx`);

  public static DownloadBpaConsolidate = (
    baseFileName: string,
    idProject: number,
    isGoalRealized: boolean,
    reportGoalType: string | null = null): Promise<void> => {
    const fileName = reportGoalType ? baseFileName + `_${reportGoalType}` : baseFileName;
    return ServiceHelper.generateFile(`${baseUrlBpaReport(idProject)}/consolidate/${isGoalRealized}`, `${fileName}_${moment().format('YYYYMMDD_HHmmss')}.xlsx`);
  };

  public static DownloadToyotaDealerEvaluation = (idProject: number, saleType: SaleTypeEnum, typeInformation: GoalVsRegistrationType): Promise<void> =>
    ServiceHelper.generateFile(
      `${baseUrlBpaReport(idProject)}/dealerEvaluation/${saleType}/${typeInformation}`,
      `Relatório_Dealer_Evaluation_v${moment().format('YYYYMMDD_HHmmss')}.xlsx`);
}
